import React, { FC } from 'react';
import s from './ErrorLayout.scss';
import ErrorButton from './ErrorButton';
import ErrorDescription from './ErrorDescription';
import { errorMapping } from './errorsMapping';
import { useTranslation } from 'react-i18next';
import dataHooks from '../../constants/data-hooks';
import classNames from 'classnames';
import { splitAndMapToDivs } from '../../../utils/helper';

interface ErrorLayoutProps {
  isNotBranded?: boolean;
}

const ErrorLayout: FC<ErrorLayoutProps> = (isNotBranded) => {
  const { t } = useTranslation();
  const { errorCode, requestId } = window.__ERROR_DATA__ || {};
  const isWixel = window.__ERROR_DATA__.brand === 'wixel';
  const isBase44 = window.__ERROR_DATA__.brand === 'base44';
  const isSymphony = window.__ERROR_DATA__.brand === 'symphony';

  if (!errorCode) {
    return null;
  }
  const { title, description, buttonLabel, buttonLink, hideCTA } = isWixel
    ? errorMapping[`${errorCode}-Wixel`] || errorMapping[errorCode]
    : isBase44
    ? errorMapping[`${errorCode}-Base44`] || errorMapping[errorCode]
    : isSymphony
    ? errorMapping[`${errorCode}-Symphony`] || errorMapping[errorCode]
    : errorMapping[errorCode];

  const cleanErrorCode = errorCode.replace('-NotBranded', '');
  const displayTitle = isBase44
    ? `${t('classic.error.title.base44')} ${cleanErrorCode}`
    : t(title);
  const displayDescription = isBase44
    ? t('classic.error.subtitle.base44')
    : t(description);
  const displayErrorCode = isBase44
    ? splitAndMapToDivs(t(description), /(?<=\.|。)/)
    : cleanErrorCode;

  const getBase44LengthModifier = () => {
    if (!isBase44) {
      return '';
    }
    const descLength = t(description).length;
    if (descLength > 80) {
      return s.base44VeryLongErrorCode;
    }
    if (descLength > 45) {
      return s.base44LongErrorCode;
    }
    return '';
  };

  return (
    <div
      className={classNames(s.content, {
        [s.premiumContent]: isNotBranded,
        [s.base44Content]: isBase44,
      })}
      data-hook={dataHooks.errorLayout}
    >
      <h2
        className={classNames({
          [s.base44ErrorTitle]: isBase44,
          [s.errorTitle]: !isBase44,
          [s.symphonyErrorTitle]: isSymphony,
        })}
      >
        {displayTitle}
      </h2>
      <h1
        className={classNames(
          {
            [s.base44ErrorCode]: isBase44,
            [s.errorCode]: !isBase44,
            [s.symphonyErrorCode]: isSymphony,
          },
          getBase44LengthModifier(),
        )}
        data-hook={dataHooks.errorCode}
      >
        {displayErrorCode}
      </h1>
      <ErrorDescription
        description={displayDescription}
        requestId={
          !errorCode.includes('404') && !isBase44 ? requestId : undefined
        }
        isBase44={isBase44}
        isSymphony={isSymphony}
      />
      {!hideCTA && <ErrorButton link={buttonLink} label={buttonLabel} />}
    </div>
  );
};

export default ErrorLayout;
